// OS CONTATOS QUE NAO SAO VENDA — cadastro e prestacao de contas na MESMA tela (migration 0275).
//
// Pedido dele em 08/09/2026: *"Preciso poder cadastrar os contatos que nao sao de venda, para que o
// sistema nao exiba aqui na Gestao. Exemplo, contatos da Cristina, Lanna e Joao, nao devem aparecer
// nos filtros das abas e nem nas estatisticas"*.
//
// ⚠️ **A coluna "Escondidos" nao e' enfeite — ela e' a razao da tela existir.** Cadastrar um numero
// aqui APAGA pedido em aberto da Gestao de Vendas, e um ato que esconde dinheiro precisa de um
// lugar onde se veja quanto foi escondido. Sem ela, um numero errado (digitado torto, ou o de uma
// pessoa que voltou a atender cliente) sumiria com vendas de verdade e ninguem teria como notar.
// Mesmo molde do "Motivos de perda": o cadastro E' o relatorio.
//
// ⚠️ **Zero escondidos e' a linha MAIS util da lista**, e por isso ela aparece em vez de sumir: ou
// aquela pessoa nao manda pedido pro proprio celular, ou o numero cadastrado nao casa com nada.
//
// ⛔ Nao ha lixeira: o numero se TIRA DA LISTA (inativa) e volta com um clique. A linha guarda por
// que aquele pedido nao aparece em lugar nenhum — apagar deixaria o sumico sem explicacao.
import { useCallback, useEffect, useState } from 'react';
import { Pencil, Plus, Eye, EyeOff } from 'lucide-react';
import { formatMoeda } from '@fiodecobre/shared';
import { upperMantendoCursor } from '../../components/inputs/UpperInput';
import { api } from '../../lib/api';
import { Modal } from '../../components/Modal';
import { ModalCadastroRapido } from '../../components/ModalCadastroRapido';
import { useToast } from '../../components/Toast';

interface Linha {
  id: number;
  numero: string;
  rotulo: string;
  ativo: boolean;
  criado_em: string;
  criado_por: string | null;
  escondidos: number;
  valor: string;
}

const PERIODOS = [30, 90, 365];

/** `556496487726` → `(64) 9648-7726`. So pra LER: o que se grava e' o numero cru. */
function formatarNumero(n: string): string {
  const d = String(n ?? '').replace(/\D/g, '');
  const semDdi = d.startsWith('55') ? d.slice(2) : d;
  if (semDdi.length < 10) return n;
  const ddd = semDdi.slice(0, 2);
  const resto = semDdi.slice(2);
  const meio = resto.length === 9 ? resto.slice(0, 5) : resto.slice(0, 4);
  return `(${ddd}) ${meio}-${resto.slice(meio.length)}`;
}

export function ModalContatosNaoVenda({ ehAdmin, onFechar }: { ehAdmin: boolean; onFechar: () => void }) {
  const toast = useToast();
  const [linhas, setLinhas] = useState<Linha[]>([]);
  const [dias, setDias] = useState(30);
  const [carregando, setCarregando] = useState(true);
  const [novo, setNovo] = useState(false);
  const [editando, setEditando] = useState<{ id: number; rotulo: string } | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const r = await api.get<{ linhas: Linha[] }>('/api/contatos-nao-venda', { query: { gestao: '1', dias } });
      setLinhas(r.linhas);
    } catch (e: any) {
      toast.erro(e?.message ?? 'Falha ao ler a lista');
    } finally {
      setCarregando(false);
    }
  }, [dias, toast]);

  useEffect(() => { void carregar(); }, [carregar]);

  async function salvarRotulo() {
    if (!editando) return;
    try {
      await api.put(`/api/contatos-nao-venda/${editando.id}`, { rotulo: editando.rotulo.trim() });
      setEditando(null);
      await carregar();
    } catch (e: any) {
      toast.erro(e?.message ?? 'Falha ao gravar');
    }
  }

  async function alternar(l: Linha) {
    try {
      await api.put(`/api/contatos-nao-venda/${l.id}`, { ativo: !l.ativo });
      await carregar();
    } catch (e: any) {
      toast.erro(e?.message ?? 'Falha ao gravar');
    }
  }

  const totalEscondidos = linhas.reduce((s, l) => s + Number(l.escondidos), 0);
  const totalValor = linhas.reduce((s, l) => s + Number(l.valor), 0);

  return (
    <Modal
      aberto
      onFechar={onFechar}
      largura={780}
      titulo="Contatos que nao sao venda"
      rodape={
        <div className="flex items-center justify-between gap-3 w-full">
          <span className="text-xs text-gray-500">
            {totalEscondidos === 0
              ? 'Nenhum pedido escondido neste periodo.'
              : `${totalEscondidos} ${totalEscondidos === 1 ? 'pedido escondido' : 'pedidos escondidos'} · ${formatMoeda(totalValor)}`}
          </span>
          <button type="button" className="btn-secundario" onClick={onFechar}>Fechar</button>
        </div>
      }
    >
      <p className="text-xs text-gray-500 mb-3">
        Orcamento enviado pra um destes numeros nao aparece na Gestao de Vendas — nem nas abas, nem
        nos contadores, nem na agenda. E o celular de quem trabalha na loja: o pedido que a vendedora
        manda pra si mesma pra separar a mercadoria nao e um cliente esperando retorno.
      </p>

      <div className="flex items-center justify-between mb-3 gap-3">
        <div className="flex items-center gap-1">
          {PERIODOS.map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDias(d)}
              className="text-xs px-2 py-1 rounded-lg"
              style={{
                border: dias === d ? '2px solid #2563eb' : '1px solid var(--border-color, #d1d5db)',
                backgroundColor: dias === d ? 'rgba(37,99,235,0.12)' : 'transparent',
                fontWeight: dias === d ? 700 : 400,
              }}
            >
              {d === 365 ? '1 ano' : `${d} dias`}
            </button>
          ))}
        </div>
        {ehAdmin && (
          <button type="button" className="btn-primario text-sm flex items-center gap-1" onClick={() => setNovo(true)}>
            <Plus size={14} /> Novo numero
          </button>
        )}
      </div>

      {carregando ? (
        <p className="text-sm text-gray-400 py-6 text-center">Carregando...</p>
      ) : linhas.length === 0 ? (
        <p className="text-sm text-gray-400 py-6 text-center">
          Nenhum numero cadastrado — tudo que sai da loja esta sendo contado como cliente.
        </p>
      ) : (
        <table className="w-full text-sm">
          <thead style={{ backgroundColor: 'var(--grid-header-bg)', color: 'var(--grid-header-fg)' }}>
            <tr>
              <th className="text-left px-3 py-2 font-medium">Quem</th>
              <th className="text-left px-3 py-2 font-medium w-40">Numero</th>
              <th className="text-right px-3 py-2 font-medium w-28">Escondidos</th>
              <th className="text-right px-3 py-2 font-medium w-32">Valor</th>
              {ehAdmin && <th className="px-3 py-2 w-56"></th>}
            </tr>
          </thead>
          <tbody>
            {linhas.map((l, idx) => (
              <tr
                key={l.id}
                className="border-b border-gray-100"
                style={{
                  backgroundColor: idx % 2 === 0 ? 'var(--grid-row-bg)' : 'var(--grid-row-alt-bg)',
                  color: 'var(--grid-row-fg)',
                  opacity: l.ativo ? 1 : 0.55,
                }}
              >
                <td className="px-3 py-2">
                  {editando?.id === l.id ? (
                    <input
                      className="input-base w-full"
                      autoFocus
                      value={editando.rotulo}
                      onChange={(e) => setEditando({ ...editando, rotulo: upperMantendoCursor(e) })}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') void salvarRotulo();
                        if (e.key === 'Escape') setEditando(null);
                      }}
                      onBlur={() => void salvarRotulo()}
                    />
                  ) : (
                    <>
                      {l.rotulo}
                      {!l.ativo && <span className="text-xs text-gray-400"> · fora da lista</span>}
                    </>
                  )}
                </td>
                <td className="px-3 py-2 text-gray-500 font-bold no-underline">{formatarNumero(l.numero)}</td>
                <td className="px-3 py-2 text-right">{l.escondidos}</td>
                <td className="px-3 py-2 text-right">{formatMoeda(Number(l.valor))}</td>
                {ehAdmin && (
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        type="button"
                        className="btn-secundario text-xs flex items-center gap-1"
                        onClick={() => setEditando({ id: l.id, rotulo: l.rotulo })}
                      >
                        <Pencil size={12} /> Renomear
                      </button>
                      <button
                        type="button"
                        className="btn-secundario text-xs flex items-center gap-1"
                        onClick={() => void alternar(l)}
                      >
                        {l.ativo ? <><Eye size={12} /> Voltar pra Gestao</> : <><EyeOff size={12} /> Esconder</>}
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {novo && (
        <ModalCadastroRapido
          titulo="Novo numero que nao e venda"
          endpoint="/api/contatos-nao-venda"
          campos={[
            { key: 'rotulo', label: 'De quem e', obrigatorio: true, descricao: 'Ex.: LANNA, JOAO, BALCAO' },
            // ⚠️ O servidor completa o DDI 55 e aceita com ou sem mascara. Cadastrar `6496487726`
            // sem completar nao casaria com nada e o cadastro pareceria ter funcionado.
            { key: 'numero', label: 'Numero com DDD', obrigatorio: true, descricao: 'Ex.: (64) 99259-7498' },
          ]}
          onFechar={() => setNovo(false)}
          onSalvo={() => { setNovo(false); void carregar(); }}
        />
      )}
    </Modal>
  );
}
